import type { TagProps } from "antd";
import { Tag as AntTag } from "antd";
import styled, { css } from "styled-components";

import { Palette } from "../../core/styles/palette";

/**
 * Variant controls the background color of the TagStyle.
 *
 * - lightPink → LightPink / white text (default)
 * - heroGreen → HeroGreen / white text
 * - warning → Warning / white text
 * - darkGreen → DarkGreen / white text
 * - aeviRed → AeviRed / white text
 * - palePink → PalePink / black text
 */

export type TagVariant = "lightPink" | "heroGreen" | "warning" | "darkGreen" | "aeviRed" | "palePink";

export interface TagComponentProps extends TagProps {
    $variant?: TagVariant;
}

const getVariantStyles = (variant?: TagVariant) => {
    switch (variant) {
        case "heroGreen":
            return css`
                background-color: ${Palette.HeroGreen};
                border-color: ${Palette.HeroGreen};
                color: ${Palette.White};
            `;
        case "darkGreen":
            return css`
                background-color: ${Palette.DarkGreen};
                border-color: ${Palette.DarkGreen};
                color: ${Palette.White};
            `;
        case "aeviRed":
            return css`
                background-color: ${Palette.AeviRed};
                border-color: ${Palette.AeviRed};
                color: ${Palette.White};
            `;
        case "warning":
            return css`
                background-color: ${Palette.Warning};
                border-color: ${Palette.Warning};
                color: ${Palette.White};
            `;
        case "palePink":
            return css`
                background-color: ${Palette.PalePink};
                border-color: ${Palette.PalePink};
                color: ${Palette.AeviBlack};
            `;
        case "lightPink":
        default:
            return css`
                background-color: ${Palette.LightPink};
                border-color: ${Palette.LightPink};
                color: ${Palette.White};
            `;
    }
};

/**
 * Unified TagStyle component that replaces FilterBadge, StatusBadge, and ValidationTag.
 *
 * Accepts all standard Ant Design Tag props plus:
 * - `$variant` – colour variant (see TagVariant)
 *
 */
export const TagStyle = styled(AntTag)<TagComponentProps>`
    border-radius: 10px;
    font-size: 12px;
    font-family: RobotoLight;
    padding: 0 14px;
    white-space: nowrap;

    ${({ $variant }) => getVariantStyles($variant)}

    .anticon-close {
        color: ${({ $variant }) => ($variant === "palePink" ? Palette.AeviBlack : Palette.White)};
    }
`;

export const TagsContainer = styled.div<{ $marginTop?: string }>`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    ${({ $marginTop }) => $marginTop && `margin-top: ${$marginTop};`}
`;
